import { create } from 'zustand';
import type { UseQueryResult } from '@tanstack/react-query';
import includes from 'lodash/includes';
import type { Loan, LoanId, Limit } from '@repo/shared/types';
import { ProviderType } from '@repo/shared/types';
import type { ResponseError } from '@repo/api/types';
import type {
  MerchantInternalError,
  MerchantInvalidSignError,
  MerchantResponse,
  ConfigType,
  MerchantQueryResultData,
} from '@repo/api/endpoints/merchant';
import { getQueryData, isUnhandledErrorQuery } from '@repo/api/endpoints/merchant';

import { VIEW_TYPE } from '@/store/view.type';
import type { ViewTypes, ViewType } from '@/store/view.type';

interface MainHubActions {
  actions: {
    setAppState: (view: ViewType, data: UseQueryResult<MerchantResponse>) => void;
    setCurrentView: (view: ViewType) => void;
    getPendingLoan: () => Loan | undefined;
    getCurrentLoan: (loanId?: LoanId) => Loan | undefined;
    getLoansLimit: () => Limit | undefined;
    getProviderConfig: (provider: ProviderType) => ConfigType | undefined;
  };
}

interface AppLoadingState {
  currentViewType: ViewTypes['APP_LOADING'];
  merchantResponseData: undefined;
  merchantResponseError: undefined;
  merchantResponseLoading: true;
}

interface AppErrorState {
  currentViewType: ViewTypes['SERVICE_NOT_AVAILABLE'];
  merchantResponseData: undefined;
  merchantResponseError: ResponseError | MerchantInvalidSignError | MerchantInternalError | true;
  merchantResponseLoading: false;
}

interface AppWithResponseDataState {
  currentViewType:
    | ViewTypes['DASHBOARD']
    | ViewTypes['LOAN_FORM_VIEW']
    | ViewTypes['LOAN_APPLICATION_POSSIBLE_WITH_SELECTED_MARKETING_DATA']
    | ViewTypes['LOAN_APPLICATION_POSSIBLE_WITHOUT_SELECTED_MARKETING_DATA']
    | ViewTypes['LOAN_APPLICATION_LOWER_PRIVILEGE_LEVEL']
    | ViewTypes['USER_HAS_LESS_THAN_3000_PRELIMIT']
    | ViewTypes['USER_HAS_NO_ACTIVE_ACCOUNT']
    | ViewTypes['PLAYGROUND']
    | ViewTypes['USER_HAS_0_PRELIMIT']
    | ViewTypes['PRAGMA_PROCESS_IFRAME']
    | ViewTypes['SELECT_OFFER_VIEW'];
  merchantResponseData: MerchantResponse;
  merchantResponseError: false;
  merchantResponseLoading: false;
}

type AppState = AppLoadingState | AppWithResponseDataState | AppErrorState;

interface MainHubState {
  merchantQueryData: MerchantQueryResultData;
  state: AppState;
}

const initialState: MainHubState = {
  merchantQueryData: undefined,
  state: {
    merchantResponseData: undefined,
    merchantResponseError: undefined,
    merchantResponseLoading: true,
    currentViewType: 'APP_LOADING',
  },
};

function returnAppState(
  viewType: ViewTypes['APP_LOADING'],
  merchantQueryData: MerchantQueryResultData,
): AppLoadingState;
function returnAppState(
  viewType: ViewTypes['SERVICE_NOT_AVAILABLE'],
  merchantQueryData: MerchantQueryResultData,
): AppErrorState;
function returnAppState(
  viewType: Exclude<ViewType, [ViewTypes['APP_LOADING'] | ViewTypes['SERVICE_NOT_AVAILABLE']]>,
  merchantQueryData: MerchantQueryResultData,
): AppWithResponseDataState;
function returnAppState(viewType: ViewType, merchantQueryData: MerchantQueryResultData): AppState {
  const merchantResponseData = getQueryData(merchantQueryData);
  if (!merchantQueryData && viewType !== VIEW_TYPE.SERVICE_NOT_AVAILABLE) {
    return {
      currentViewType: VIEW_TYPE.APP_LOADING,
      merchantResponseData: undefined,
      merchantResponseLoading: true,
      merchantResponseError: undefined,
    };
  }

  if (viewType === VIEW_TYPE.APP_LOADING) {
    return {
      currentViewType: VIEW_TYPE.APP_LOADING,
      merchantResponseData: undefined,
      merchantResponseLoading: true,
      merchantResponseError: undefined,
    };
  }

  if (isUnhandledErrorQuery(merchantQueryData)) {
    return {
      currentViewType: VIEW_TYPE.SERVICE_NOT_AVAILABLE,
      merchantResponseData: undefined,
      merchantResponseError: merchantQueryData?.error || true,
      merchantResponseLoading: false,
    };
  }

  if (viewType === VIEW_TYPE.SERVICE_NOT_AVAILABLE) {
    return {
      currentViewType: VIEW_TYPE.SERVICE_NOT_AVAILABLE,
      merchantResponseData: undefined,
      merchantResponseError: merchantQueryData?.error || true,
      merchantResponseLoading: false,
    };
  }

  if (
    (!includes(viewType, VIEW_TYPE.SERVICE_NOT_AVAILABLE) ||
      !includes(viewType, VIEW_TYPE.APP_LOADING)) &&
    merchantResponseData &&
    merchantResponseData !== 'RESPONSE_422_VALIDATION_ERROR'
  ) {
    return {
      currentViewType: viewType,
      merchantResponseData,
      merchantResponseError: false,
      merchantResponseLoading: false,
    };
  }

  throw new Error('Invalid state or view type');
}

export const mainHubStore = create<MainHubState & MainHubActions>((set, get) => ({
  ...initialState,
  actions: {
    setAppState: (viewType, queryData) => {
      set(() => {
        return {
          merchantQueryData: queryData,
          state: returnAppState(viewType, queryData),
        };
      });
    },
    setCurrentView: (viewType) => {
      set(({ merchantQueryData }) => {
        return {
          state: returnAppState(viewType, merchantQueryData),
        };
      });
    },
    getPendingLoan: () =>
      get().state.merchantResponseData?.data.loans.find((loan) => loan.stage < 5),
    getCurrentLoan: (loanId) =>
      get().state.merchantResponseData?.data.loans.find((loan) => loan.id === loanId),
    getLoansLimit: () =>
      get().state.merchantResponseData?.data.limits.find((loan) => Boolean(loan)),
    getProviderConfig: (provider: ProviderType) => {
      return get().state.merchantResponseData?.config.find(
        (providersConfig) => providersConfig.provider === provider,
      );
    },
  },
}));

export const useMainHubStoreActions = () => mainHubStore((state) => state.actions);
export const useMainHubStoreState = () => mainHubStore((state) => state.state);

export function useMainHubStoreAvailableResponseDataState(
  currentView: ViewTypes['APP_LOADING'],
): AppLoadingState;
export function useMainHubStoreAvailableResponseDataState(
  currentView: ViewTypes['SERVICE_NOT_AVAILABLE'],
): AppErrorState;
export function useMainHubStoreAvailableResponseDataState(
  currentView: Exclude<ViewType, ViewTypes['APP_LOADING'] | ViewTypes['SERVICE_NOT_AVAILABLE']>,
): AppWithResponseDataState;
export function useMainHubStoreAvailableResponseDataState(currentView: ViewType): AppState {
  const appState = useMainHubStoreState();

  if (appState.currentViewType === 'APP_LOADING') {
    return appState;
  }

  if (appState.currentViewType === 'SERVICE_NOT_AVAILABLE') {
    return appState;
  }

  if (appState.currentViewType === currentView) {
    return appState;
  }

  if (appState.currentViewType === 'PLAYGROUND') {
    return appState;
  }

  throw new Error('Invalid state or view type');
}
